import {
  Button,
  Card,
  CardContent,
  CardHeader,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  makeStyles,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import clsx from 'clsx';
import React, {useEffect, useState} from 'react';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import {AerialAreaMeasurement, AerialMeasurement,
  Measurement3D, MeasurementArea, MeasurementPoint,
  MeasurementVolume} from '../viewer/js/measurements/measurement';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import 'font-awesome/css/font-awesome.css';
import { nanoid } from '@reduxjs/toolkit';
import { AlertDialog, PromptDialog, TextDialog } from '../dialogs';
import { imageOnlyDistance } from '../viewer/js/measurements/image-only';
import unitConverter from '../viewer/js/utilities/unit-converter';
import { dialog, fs, isStaticSite } from "../electron-modules";
import { useSnackbar } from 'notistack';
import slash from 'slash';
import fileDialog from 'file-dialog';
import { useSelector, useDispatch } from 'react-redux';
import { selectMeasureUnits, changeMeasureUnits } from '../redux/projections-slice';
import LocalScene from '../viewer/js/projections';
import { ArrowTooltip } from '../tooltips';
import { DenseDivider, IconToolBar, SlimScrollbar, ToolBarButton, ToolBarDivider } from '../toolbar';
import { Viewer } from '../viewer/js/main';

const copyTextToClipboard = require('copy-to-clipboard');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    vertexWrapped: {
      float: "right"
    },
    textField: {
      marginBottom: theme.spacing(3)
    },
    userWarning: {
      padding: theme.spacing(2)
    },
    cardButtons: {
      marginTop: 0,
      alignSelf: "center"
    },
    card: {
      margin: theme.spacing(1.5),
      userSelect: "none"
    },
    footerDiv: {
      width: "100%",
      boxShadow: "0px 10px 10px 2px",
      zIndex: 1,
      textAlign: "center"
    },
    measurementUnitsParent: {
      justifyContent: "center"
    },
    measurementUnitsFlex: {
      flex: 1,
      justifyContent: "center"
    },
    measurementUnitsRadio: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5)
    },
    imageOnlyInfoButton: {
      marginLeft: theme.spacing(1)
    },
    marginside: {
      margin: 0,
      padding: "10px"
    },
    actions: {
      padding: "5px"
    },
    expandText: {
      width: "100%",
      display: "block",
      textAlign: "center",
      cursor: "pointer",
      userSelect: "none",
      paddingBottom: "5px",
      paddingTop: "5px"
    },
    bold: {
      fontWeight: "bold"
    },
    tableContent: {
      backgroundColor: "white",
      padding: "0px !important",
    },
    cardHeader: {
      padding: "10px"
    },
    cell: {
      fontSize: "0.75em",
      padding: "6px 10px"
    },
    headerTitle: {
      fontWeight: "bold",
      cursor: "pointer"
    },
    calculateButton: {
      padding: "2px 4px",
      fontSize: "0.7rem"
    },
    recalculate: {
      display: "flex",
      flexDirection: "row-reverse",
      alignItems: "center"
    },
    recalculateButton: {
      padding: "5px !important"
    },
    copyClipboard: {
      cursor: "pointer"
    },
    measureList: {
      padding: "0px",
      overflow: "auto",
      flex: 1
    },
    lastTableRow: {
      borderBottom: "0px"
    },
  })
);

const ExportDialog = (props) => {
  const viewer = props.viewer as Viewer;
  const {open, classes, onClose} = props;
  const [exportType, setExportType] = useState("csv");
  const [ordering, setOrdering] = useState("xyz");

  const onSubmit = () => {
    viewer?.exportMeasurements(exportType, ordering);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        Measurements Export
      </DialogTitle>

      <DialogContent>
        {/* Export type */}
        <InputLabel>Export Type</InputLabel>
        <Select
          fullWidth
          value={exportType}
          className={classes.textField}
          onChange={(event) => {
            setExportType(event.target.value as string);
          }}
        >
          <MenuItem  value={"csv"}>CSV File</MenuItem>
          <MenuItem  value={"dat"}>Measure Data</MenuItem>
        </Select>

        {exportType === "csv" && (
          <React.Fragment>
            {/* Ordering */}
            <InputLabel>Ordering</InputLabel>
            <Select
              fullWidth
              value={ordering}
              className={classes.textField}
              onChange={(event) => {
                setOrdering(event.target.value as string);
              }}
            >
              <MenuItem  value={"xyz"}>Easting, Northing, Elevation</MenuItem>
              <MenuItem  value={"yxz"}>Northing, Easting, Elevation</MenuItem>
            </Select>
          </React.Fragment>
        )}

        {exportType !== "csv" && (
          <Typography variant="caption">The file generated using "Measure Data" options can be
          re-imported as a new measurement group in any project</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          color="primary"
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MeasureTypeIcon = (props) => {
  const {type, icon, measureType, setMeasureType} = props;

  // Remove hyphen and capitalize each word
  const title = type
    .replace("-", " ")
    .replace(/(^\w|\s\w)/g, m => m.toUpperCase());

  const onClick = () => {
    setMeasureType(type);
  };

  return (
    <ToolBarButton
      title={title}
      icon={icon}
      onClick={onClick}
      color={measureType===type ? "secondary" : "primary"}
    />
  );
};

const ImportExportIcon = (props) => {
  const {icon, onClick} = props;

  return (
    <ToolBarButton
      title={"Import/Export measurements"}
      icon={icon}
      onClick={onClick}
      success
    />
  );
};

const MeasurementCard = (props) => {
  const viewer = props.viewer as Viewer;
  const {measurement, classes, first} = props;

  const showMessage = useSnackbar().enqueueSnackbar;
  const [expanded, setExpanded] = useState(false);
  const [hover, setHover] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const formatter = measurement.formatter;

  const onMouseOver = () => {
    setHover(true);
  };

  const onMouseLeave = () => {
    setHover(false);
  };

  const generateVertexPositions = () => {
    let rows = [];

    const precision = formatter.getVectorPrecision(LocalScene.viewProjection);
    const verticesReprojected = measurement.vertices.map(vertex => {
      return vertex.toViewProjection();
    });

    verticesReprojected.forEach((vertex, index) => {
      const x = vertex.x.toFixed(precision.x);
      const y = vertex.y.toFixed(precision.y);
      const z = vertex.z.toFixed(precision.z);

      let type = verticesReprojected.length > 1 ?
        `Vertex ${index + 1}` : "Position";

      rows.push({
        type: type,
        value: (
          <div
            className={classes.copyClipboard}
            onClick={(event) => {
              event.stopPropagation();
              let x = vertex.x.toFixed(precision.x);
              let y = vertex.y.toFixed(precision.y);
              let z = vertex.z.toFixed(precision.z);
              let text = `(${x}, ${y}, ${z})`;
              copyTextToClipboard(text);
              showMessage("Copied to clipboard", {
                variant: "success"
              });
            }}
          >
            <ArrowTooltip title="Click to copy to clipboard">
              <div className={classes.vertexWrapped}>
                <b>X:</b> {x}<br/>
                <b>Y:</b> {y}<br/>
                <b>Z:</b> {z}<br/>
              </div>
            </ArrowTooltip>
          </div>
        )
      });
    });

    return rows;
  };

  const getVolumeResults = () => {
    let volumeValue = measurement.invalidVolumeMessage;

    if (measurement.valid) {
      if (measurement.calculating) {
        volumeValue = (
          <Button
            size="small"
            disabled
            className={classes.calculateButton}
          >
            Calculating...
          </Button>
        );
      } else if (measurement.calculated) {
        const value = formatter.formatVolume(measurement);
        volumeValue = (
          <div className={classes.recalculate}>
            <div dangerouslySetInnerHTML={{__html: value}}/>
            <ArrowTooltip title="Recalculate Volume">
              <IconButton
                className={classes.recalculateButton}
                onClick={(event) => {
                  event.stopPropagation();
                  measurement.recalculate();
                }}
              >
                <RotateRightIcon fontSize="small"/>
              </IconButton>
            </ArrowTooltip>
          </div>
        );
      } else {
        volumeValue = (
          <ArrowTooltip title="Calculate Volume">
            <Button
              color="primary"
              size="small"
              className={classes.calculateButton}
              onClick={(event) => {
                event.stopPropagation();
                measurement.calculate();
              }}
            >
              Calculate
            </Button>
          </ArrowTooltip>
        );
      }
    }

    return volumeValue;
  };

  const generateMeasurementRows = () => {
    let rows = [];

    if (measurement instanceof MeasurementArea) {
      let areaValue = measurement.valid ?
        formatter.formatArea(measurement.area3D) :
        measurement.areaInvalidMessage;
      rows.push({type: "Area", value: areaValue});

      if (measurement instanceof MeasurementVolume) {
        let volumeValue = getVolumeResults();
        rows.push({type: "Volume", value: volumeValue});
      }
    } else if (measurement instanceof MeasurementPoint) {
      // Do nothing for points
    } else if (measurement instanceof Measurement3D) {
      rows.push({type: "Distance (2D)", value: formatter.formatLength(measurement.distance2D)});
      rows.push({type: "Distance (3D)", value: formatter.formatLength(measurement.distance3D)});
      rows.push({type: "Grade", value: formatter.formatGrade(measurement.grade)});
      rows.push({type: "Angle", value: formatter.formatAngle(measurement.angle)});
    } else if (measurement instanceof AerialMeasurement) {
      if (measurement instanceof AerialAreaMeasurement) {
        rows.push({
          type: measurement.displayType,
          value: formatter.formatArea(measurement.getValue())
        });
      } else {
        rows.push({
          type: measurement.displayType,
          value: formatter.formatLength(measurement.getValue())
        });
      }
    }

    if (measurement instanceof Measurement3D) {
      rows.push(...generateVertexPositions());
    }

    return rows;
  };

  const createMarkup = (content) => {
    if (typeof(content) === "string") {
      return content ?
        (<div dangerouslySetInnerHTML={{__html: content}}/>) :
        "";
    }

    return content;
  };

  const handleRenameSubmit = name => {
    setRenameOpen(false);
    viewer?.renameMeasurement(measurement, name);
  };

  const handleDeleteSubmit = () => {
    setDeleteOpen(false);
    viewer?.deleteMeasurement(measurement);
  };

  useEffect(() => {
    viewer?.updateMeasureHover(measurement, hover);
  }, [viewer, hover]);

  useEffect(() => {
    setExpanded(first);
  }, [first]);

  let rows = generateMeasurementRows();

  return (
    <React.Fragment>
      <Card
        className={classes.card}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        raised={hover}
      >
        {/* Header */}
        <CardHeader
          className={classes.cardHeader}
          classes={{
            action: classes.cardButtons
          }}
          action={
            <React.Fragment>
              {/* Expand/collapse measurement */}
              <ArrowTooltip title={expanded? "Collapse" : "Expand"}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setExpanded(!expanded);
                  }}
                >
                  {expanded && <ExpandLessIcon fontSize="small"/>}
                  {!expanded && <ExpandMoreIcon fontSize="small"/>}
                </IconButton>
              </ArrowTooltip>

              {/* Rename measurement */}
              <ArrowTooltip title="Rename">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setRenameOpen(true);
                  }}
                >
                  <EditIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

              {/* Delete measurement */}
              <ArrowTooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteOpen(true);
                  }}
                >
                  <DeleteIcon fontSize="small"/>
                </IconButton>
              </ArrowTooltip>

            </React.Fragment>
          }
          title={measurement.title}
          titleTypographyProps={{
            variant: "body2",
            className: classes.headerTitle
          }}
          subheader={`Total Vertices: ${measurement.numVertices}`}
          subheaderTypographyProps={{
            variant: "caption"
          }}
        />

        {/* Expandable content */}
        {expanded && (<React.Fragment>
          <CardContent className={classes.tableContent}>

            {/* Measurement values table */}
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {rows.map((row, index) => {
                    const isLastRow = index === (rows.length - 1);

                    return (
                      <TableRow key={nanoid()}>
                        <TableCell
                          className={clsx(classes.cell, classes.bold, {
                            [classes.lastTableRow]: isLastRow
                          })}
                          component="th" scope="row"
                        >
                          {row.type}
                        </TableCell>

                        <TableCell
                          className={clsx(classes.cell, {
                            [classes.lastTableRow]: isLastRow
                          })}
                          align="right"
                        >
                          {createMarkup(row.value)}
                        </TableCell>

                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

          </CardContent>
        </React.Fragment>)}
      </Card>

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteOpen}
        onSubmit={handleDeleteSubmit}
        onCancel={() => {
          setDeleteOpen(false);
        }}
        title="Delete entry?"
        prompt="Do you want to delete this measurement?"
        button="Delete"
      />

      {/* Rename Dialog */}
      <TextDialog
        open={renameOpen}
        onSubmit={handleRenameSubmit}
        onCancel={() => {
          setRenameOpen(false);
        }}
        title="Rename Measurement"
        prompt="Enter new name"
        default={measurement.title}
        label="Measurement Name"
      />
    </React.Fragment>
  );
};

export const Measurements = (props) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const showMessage = useSnackbar().enqueueSnackbar;

  const viewer = props.viewer as Viewer;
  const {active, measurements} = props;

  const measurementUnits = useSelector(selectMeasureUnits);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [measureType, setMeasureType] = useState("standard");
  const [measureUnits, setMeasureUnits] = useState('m');
  const [contextState, setContextState] = useState({
    mouseX: null,
    mouseY: null,
  });

  const loadImportData = (text) => {
    let encoded = decodeURIComponent(text);
    let data = JSON.parse(encoded);
    let response = viewer.loadMeasurementData(data);

    if (response.success) {
      showMessage(response.message, {
        variant: "success"
      });
    } else {
      showMessage(response.message, {
        variant: "error"
      });
    }
  };

  const openImportElectron = () => {
    const fileFilter = viewer?.getMeasureExportType();

    dialog.showOpenDialog( {
      filters: fileFilter,
      properties: ['openFile']
    }).then(result => {
      if (result.canceled) {
        return;
      }

      if (result.filePaths) {
        const filePath = slash(result.filePaths[0]);
        fs.readFile(filePath, 'utf-8', (err, text) => {
          if (err) {
            dialog.showErrorBox('Error loading measurement data', 'An error occurred while loading the measurement file.');
          }

          loadImportData(text);
        });
      }
    }).catch(() => {
      dialog.showErrorBox('Error', 'An error occurred while loading the specified file.');
    });
  };

  const openImportBrowser = () => {
    const fileFilter = viewer?.getMeasureExportType();
    const fileType = fileFilter[0].extensions[0];

    fileDialog({accept: `.${fileType}`})
      .then(files => {
        if (files.length === 0) {
          return;
        }

        let reader = new FileReader();
        reader.onload = event => {
          let text = event.target.result;
          loadImportData(text);
        };
        reader.readAsText(files[0]);
      });
  };

  const openExportMenu = (event) => {
    setContextState({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const closeExportMenu = () => {
    setContextState({
      mouseX: null,
      mouseY: null,
    });
  };

  const onExport = () => {
    closeExportMenu();
    setExportModalOpen(true);
  };

  const onImport = () => {
    closeExportMenu();

    if (isStaticSite) {
      openImportBrowser();
    } else {
      openImportElectron();
    }
  };

  const handleUnitsChange = (event) => {
    const value = event.target.value;
    dispatch(changeMeasureUnits(value));
  };

  useEffect(() => {
    viewer?.toggleMeasurements(active);
    viewer?.updateMeasureType(measureType);
  }, [viewer, active]);

  useEffect(() => {
    viewer?.updateMeasureType(measureType);
  }, [viewer, measureType]);

  useEffect(() => {
    setMeasureUnits(measurementUnits);
    viewer?.setMeasurementUnits(measurementUnits);
  }, [viewer, measurementUnits]);

  const reversed = [...measurements].reverse();
  const iconProps = {classes, measureType, setMeasureType};
  const menuOpen = contextState.mouseY !== null;
  const [imageOnlyWarningOpen, setImageOnlyWarningOpen] = useState(false);
  const anchorPosition = contextState.mouseY !== null && contextState.mouseX !== null
    ? { top: contextState.mouseY, left: contextState.mouseX }
    : undefined;

  const exportDisabled = measurements.length === 0;
  const importDisabled = viewer?.hasCameras || viewer?.hasPoints;
  const isImageOnly = viewer?.isImageOnly;

  const imageDistanceText = unitConverter.convert(imageOnlyDistance,
    'm', measurementUnits).toFixed(2);

  return (
    <React.Fragment>
      {/* Measurement icons */}
      <IconToolBar>
        <MeasureTypeIcon type="standard" icon="fa-ellipsis-h" {...iconProps}/>
        <MeasureTypeIcon type="single-point" icon="fa-circle-o" {...iconProps}/>
        {(isImageOnly === false) && (<MeasureTypeIcon type="snap-up" icon="fa-arrow-up" {...iconProps}/>)}
        {(isImageOnly === false) && (<MeasureTypeIcon type="snap-down" icon="fa-arrow-down" {...iconProps}/>)}
        <MeasureTypeIcon type="area" icon="fa-square-o" {...iconProps}/>
        {(isImageOnly === false) && (<MeasureTypeIcon type="volume" icon="fa-codepen" {...iconProps}/>)}
        <ToolBarDivider/>
        <ImportExportIcon
          onClick={openExportMenu}
          icon="fa-download"
          {...iconProps}
        />
      </IconToolBar>

      <DenseDivider variant="middle" spacing={5}/>

      {/* Measurement list */}
      <SlimScrollbar>
        <List dense className={classes.measureList}>
          {measurements.length === 0 && (<Typography variant="subtitle2" className={classes.userWarning}>
            No measurements have been added.
          </Typography>)}

          {reversed.map((measurement, index) => {
            return (
              <MeasurementCard
                key={measurement.id}
                viewer={viewer}
                first={index===0}
                measurement={measurement}
                classes={classes}
              />
            );
          })}
        </List>
      </SlimScrollbar>

      {/* Measurement footer */}
      <div className={classes.footerDiv}>

        {/* Measurement units */}
        <div >
          <RadioGroup
            aria-label="anonymous"
            name="measure-units"
            value={measureUnits}
            onChange={handleUnitsChange}
            className={classes.measurementUnitsParent}
            row
          >
            <FormControlLabel
              value="m"
              className={classes.measurementUnitsFlex}
              control={<Radio size="small" classes={{root: classes.measurementUnitsRadio}}/>}
              label={<Typography variant="caption">Metric</Typography>}
            />

            <FormControlLabel
              value="ft"
              className={classes.measurementUnitsFlex}
              control={<Radio size="small" classes={{root: classes.measurementUnitsRadio}}/>}
              label={<Typography variant="caption">Imperial</Typography>}
            />
          </RadioGroup>
        </div>

        {/* Image only warning */}
        {isImageOnly && (<div>
          <Typography variant="caption">Image-only measurements enabled</Typography>
          <IconButton
            className={classes.imageOnlyInfoButton}
            size="small"
            color="secondary"
            onClick={() => {
              setImageOnlyWarningOpen(true);
            }}
          >
            <InfoIcon fontSize="small"/>
          </IconButton>
        </div>)}

      </div>

      {/* Import/Export menu */}
      <Menu
        keepMounted
        open={menuOpen}
        onClose={closeExportMenu}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
      >
        <MenuItem
          disabled={exportDisabled}
          onClick={onExport}
        >
          Export Measurements
        </MenuItem>
        <MenuItem
          disabled={!importDisabled}
          onClick={onImport}
        >
          Import Measurements
        </MenuItem>
      </Menu>

      {/* Export Measurements */}
      <ExportDialog
        open={exportModalOpen}
        viewer={viewer}
        classes={classes}
        onClose={() => {
          setExportModalOpen(false);
        }}
      />

      {/* Image only warning */}
      <AlertDialog
        open={imageOnlyWarningOpen}
        title="Image Only Measurements"
        handleClose={() => {
          setImageOnlyWarningOpen(false);
        }}
      >
        <DialogContentText>
        Image-only measurements are calculated by intersecting a flat plane at the approximate
        ground height, based on the provided "sensor height" project configuration value.
        </DialogContentText>
        <br/>
        <DialogContentText>
        Due to the lowered accuracy further away from the camera position, the measurements
        clicks are restricted to <b>{imageDistanceText}{measurementUnits}</b> from the current
        camera position`
        </DialogContentText>
      </AlertDialog>
    </React.Fragment>
  );
};
